import * as React from 'react';
import { useMemo } from 'react';

import { titleize } from '@admin/components/helpers/titleize';
import { client } from '@admin/libraries/apollo';
import { useRoomCategoriesQuery, RoomCategoriesQuery } from '@admin/schema';

type RoomCategory = NonNullable<RoomCategoriesQuery['roomCategories']>[0];

interface RoomSelection {
  id: string;
  quantity: number;
  selected: boolean;
  // From Rails API via Jbuilder
  room_category_id?: string;
  // From GraphQL/shared types
  categoryID?: string;
  // Full category object when preloaded
  category?: RoomCategory;
}

interface RoomSelectionsPreviewProps {
  resource: {
    // Support both naming conventions
    room_selections?: RoomSelection[];
    roomSelections?: RoomSelection[];
  };
}

/**
 * Displays a preview of selected room categories with their quantities
 * Fetches room categories via GraphQL and matches them with room selections by ID
 * Supports both snake_case (Angular) and camelCase (GraphQL) property naming
 * @param resource - The resource containing room_selections/roomSelections array
 */
export const RoomSelectionsPreview: React.FC<RoomSelectionsPreviewProps> = ({ resource }) => {
  const { data, loading, error } = useRoomCategoriesQuery({ client });

  const filteredSelections = useMemo(() => {
    const roomSelections = resource?.roomSelections || resource?.room_selections;
    const roomCategories = data?.roomCategories || [];

    if (!roomSelections?.length || !roomCategories.length || loading || error) {
      return [];
    }

    return roomSelections
      .filter((selection) => selection.quantity > 0 || selection.selected)
      .map((selection) => {
        // Use the category from the selection if available, otherwise look it up by ID
        let category = selection.category;
        if (!category) {
          const categoryID = selection.categoryID || selection.room_category_id;
          if (categoryID) {
            category = roomCategories.find((cat) => cat.id === categoryID);
          }
        }
        return {
          ...selection,
          roomCategory: category,
        };
      })
      .filter((selection) => selection.roomCategory); // Only include selections with valid categories
  }, [resource?.roomSelections, resource?.room_selections, data?.roomCategories, loading, error]);

  if (error) {
    console.error('Failed to fetch room categories:', error);
    return null;
  }

  if (loading) {
    return null;
  }

  if (filteredSelections.length === 0) {
    return null;
  }

  return (
    <div>
      {filteredSelections.map((roomSelection) => (
        <div key={roomSelection.id}>
          {roomSelection.roomCategory?.countable && <span>{roomSelection.quantity} </span>}
          <span>{roomSelection.roomCategory?.name && titleize(roomSelection.roomCategory.name)}</span>
        </div>
      ))}
    </div>
  );
};
